/**
 * Stylitics "Wear It With" Widget for Faherty
 * Enhanced version with robust container handling and fallback creation
 */

class StyliticsWidget {
  constructor() {
    this.composer = null;
    this.productId = null;
    this.settings = window.styliticsSettings || {};
    this.isInitialized = false;
    this.retryCount = 0;
    this.maxRetries = 3;

    console.log("Stylitics Widget Initializing...", this.settings);
  }

  /**
   * Initialize the widget
   */
  async init() {
    try {
      // Wait for DOM to be ready
      if (document.readyState === "loading") {
        await new Promise((resolve) => {
          document.addEventListener("DOMContentLoaded", resolve);
        });
      }

      // Additional wait for theme loading
      await this.waitForElement("body");

      await this.detectProductId();
      if (!this.productId) {
        console.warn("No product ID detected. Stylitics widget will not load.");
        return;
      }

      await this.ensureContainer();
      await this.waitForComposer();
      await this.initializeWidget();
    } catch (error) {
      console.error("Error initializing Stylitics widget:", error);

      // Retry logic
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        console.log(
          `Retrying Stylitics initialization (attempt ${this.retryCount}/${this.maxRetries})`
        );
        setTimeout(() => this.init(), 2000);
      }
    }
  }

  /**
   * Ensure the container exists, create if missing
   */
  async ensureContainer() {
    let container = document.getElementById("stylitics-composer-container");

    if (!container) {
      console.log("Container not found, creating fallback...");
      container = this.createFallbackContainer();
    }

    if (!container) {
      throw new Error("Unable to create or find Stylitics container");
    }

    console.log("Container ready:", container);
    return container;
  }

  /**
   * Create fallback container if template doesn't include one
   */
  createFallbackContainer() {
    // Try to find a good insertion point
    const insertionPoints = [
      // After buy buttons
      document.querySelector(".product-info__buy-buttons"),
      document.querySelector('[data-block-type="buy_buttons"]'),
      // After product info
      document.querySelector(".product-info"),
      // After main product content
      document.querySelector(".shopify-section--main-product .product"),
      // After any product section
      document.querySelector(".shopify-section--main-product"),
      // Fallback to main content
      document.querySelector("main"),
      document.querySelector("#main-content"),
      document.body,
    ];

    const insertAfter = insertionPoints.find((el) => el !== null);

    if (!insertAfter) {
      console.error(
        "Could not find suitable insertion point for Stylitics container"
      );
      return null;
    }

    // Create container with proper styling
    const container = document.createElement("div");
    container.id = "stylitics-composer-container";
    container.className = "gutters margin-top--l stylitics-fallback-container";
    container.style.marginTop = "2rem";
    container.style.scrollMarginTop = "100px";

    // Insert after the found element
    insertAfter.parentNode.insertBefore(container, insertAfter.nextSibling);

    console.log("Created fallback container after:", insertAfter.tagName);
    return container;
  }

  /**
   * Detect current product ID using multiple methods
   */
  async detectProductId() {
    // Method 1: Shopify Analytics
    if (window.ShopifyAnalytics?.meta?.product?.id) {
      this.productId = window.ShopifyAnalytics.meta.product.id.toString();
      console.log("Product ID from Shopify Analytics:", this.productId);
      return;
    }

    // Method 2: Meta property
    const metaProductId = document.querySelector(
      'meta[property="product:retailer_item_id"]'
    );
    if (metaProductId) {
      this.productId = metaProductId.getAttribute("content");
      console.log("Product ID from meta:", this.productId);
      return;
    }

    // Method 3: Global product object
    if (window.product?.id) {
      this.productId = window.product.id.toString();
      console.log("Product ID from window.product:", this.productId);
      return;
    }

    // Method 4: Theme global
    if (window.theme?.product?.id) {
      this.productId = window.theme.product.id.toString();
      console.log("Product ID from theme:", this.productId);
      return;
    }

    // Method 5: Script tag data
    const productScripts = document.querySelectorAll(
      'script[type="application/ld+json"]'
    );
    for (const script of productScripts) {
      try {
        const data = JSON.parse(script.textContent);
        if (data["@type"] === "Product" && data.productID) {
          this.productId = data.productID.toString();
          console.log("Product ID from JSON-LD:", this.productId);
          return;
        }
      } catch (e) {
        // Continue searching
      }
    }

    console.warn("Could not detect product ID");
  }

  /**
   * Wait for StyliticsComposer to be available
   */
  async waitForComposer(timeout = 10000) {
    const startTime = Date.now();

    while (!window.StyliticsComposer) {
      if (Date.now() - startTime > timeout) {
        throw new Error("StyliticsComposer not loaded within timeout");
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    console.log("StyliticsComposer available");
  }

  /**
   * Wait for element to exist
   */
  async waitForElement(selector, timeout = 5000) {
    const startTime = Date.now();

    while (!document.querySelector(selector)) {
      if (Date.now() - startTime > timeout) {
        throw new Error(`Element ${selector} not found within timeout`);
      }
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    return document.querySelector(selector);
  }

  /**
   * Initialize the Stylitics widget
   */
  async initializeWidget() {
    try {
      // Get customer data - prioritize reliable Liquid-generated data
      const customerData = window.styliticsCustomer || {};
      const customerId = customerData.id || window.__VPV?.customer?.id || null;
      const customerEmail =
        customerData.email || window.__VPV?.customer?.email || null;

      console.log("Initializing Stylitics widget with:", {
        productId: this.productId,
        customerId: customerId,
        customerEmail: customerEmail,
        settings: this.settings,
      });

      this.composer = await StyliticsComposer.initialize({
        client: "faherty",
        target: "stylitics-composer-container",
        config: {
          modules: [
            {
              type: "classic",
              params: {
                api: {
                  item_number: this.productId,
                  min: this.settings.minBundles || 3,
                  max: this.settings.maxBundles || 6,
                },
                text: {
                  itemLinkCTA: this.settings.buttonText || "Shop",
                  moduleHeading: this.settings.heading || "Wear It With",
                  moduleHeadingLevel: "h4",
                  modalHeadingLevel: "h6",
                },
              },
              on: {
                "mount.bundles": (data) => this.handleBundlesMount(data),
                "mount.bundle": (data) => this.handleBundleMount(data),
                "mount.error": (error) => {
                  console.error("Stylitics mount error:", error);
                },
                "api.error": (error) => {
                  console.error("Stylitics API error:", error);
                },
                "api.request": (request) => {
                  console.log("Stylitics API request:", request);
                },
                "api.response": (response) => {
                  console.log("Stylitics API response:", response);
                },
                // NEW TRACKING EVENTS
                "click.product": (event) => this.handleProductClick(event),
                "click.hotspot": (event) => this.handleHotspotClick(event),
                "click.addToCart": (event) => this.handleAddToCartClick(event),
              },
            },
          ],
          sharedParams: {
            customer: customerId
              ? {
                  clientUserId: customerId,
                  ...(customerEmail && { email: customerEmail }),
                }
              : {},
            analytics: {
              placement: "pdp",
              // Enable comprehensive tracking
              enableClickTracking: true,
              enableViewTracking: true,
              enableEngagementTracking: true,
            },
          },
        },
      });

      // CRITICAL: Start the composer to trigger API calls and bundle loading
      console.log("Starting Stylitics composer...");
      await this.composer.start();
      console.log("Stylitics composer started - API calls should now be made");

      this.isInitialized = true;
      this.bindJumpLinkTracking();
      this.setupQuickAddInterception();
      this.setupVariantChangeListener();
      this.setupAddToCartTracking();

      // Initialize interaction tracking
      this._lastStyliticsInteraction = null;
      this._quickViewFromStylitics = false;
      this._quickViewOpenTime = null;

      console.log("Stylitics widget initialized successfully");
    } catch (error) {
      console.error("Error initializing Stylitics widget:", error);
      throw error;
    }
  }

  /**
   * Handle bundles mount event
   */
  handleBundlesMount(data) {
    console.log("Bundles loaded:", data.bundles?.length || 0);
    console.log("Full bundles data:", data);

    if (!data.bundles || data.bundles.length === 0) {
      console.log("No bundles available for this product");
      console.log("Possible reasons:");
      console.log("- Product not in Stylitics catalog");
      console.log("- No outfit combinations created");
      console.log("- API temporarily unavailable");
      console.log("- Product ID mismatch");
      this.hideWidget();
      return;
    }

    const minBundles = this.settings.minBundles || 3;

    console.log(
      `Bundle threshold check: ${data.bundles.length} bundles found, minimum required: ${minBundles}`
    );

    if (data.bundles.length >= minBundles) {
      console.log("Threshold met - showing widget");
      this.showWidget();
      this.createJumpLinkIfNeeded();

      // Log detailed bundle information
      data.bundles.forEach((bundle, index) => {
        console.log(`Bundle ${index + 1}:`, {
          id: bundle.id,
          name: bundle.name,
          products: bundle.products?.length || 0,
          hasOnModelImage: !!bundle["on-model-image"],
        });
      });

      // Check container content after a short delay
      setTimeout(() => {
        const container = document.getElementById(
          "stylitics-composer-container"
        );
        console.log("Container status after bundle mount:");
        console.log("- Child nodes:", container.childNodes.length);
        console.log("- Inner HTML length:", container.innerHTML.length);
        console.log(
          "- Has Stylitics elements:",
          container.querySelectorAll('[class*="stylitics"]').length
        );

        if (container.innerHTML.length > 100) {
          console.log("SUCCESS: Widget content loaded!");
        } else {
          console.log("Widget initialized but content still loading...");
        }
      }, 2000);
    } else {
      console.log(
        `Not enough bundles (${data.bundles.length} < ${minBundles}), hiding widget`
      );
      this.hideWidget();
    }
  }

  /**
   * Handle individual bundle mount for custom badges
   */
  handleBundleMount(data) {
    if (!data.bundle || !data.element) return;

    // Add "Style the Model" badge for on-model images
    if (data.bundle["on-model-image"]) {
      const badgeContainer = data.element.querySelector(
        ".stylitics-bundle-badge"
      );
      if (
        badgeContainer &&
        !badgeContainer.querySelector(".stylitics-stm-badge")
      ) {
        const badge = document.createElement("img");
        badge.className = "stylitics-stm-badge";
        badge.src =
          "https://cdn.shopify.com/s/files/1/0359/8357/files/STM.png?v=1736387449";
        badge.alt = "This outfit corresponds with model's on product image";
        badgeContainer.appendChild(badge);
      }
    }
  }

  /**
   * Show the widget and jump link
   */
  showWidget() {
    const container = document.getElementById("stylitics-composer-container");
    if (container) {
      container.style.display = "block";
      container.style.visibility = "visible";
    }

    const jumpLink = document.getElementById("stylitics-jumplink");
    if (jumpLink) {
      jumpLink.style.visibility = "visible";
      jumpLink.style.display = "block";
    }

    // Show widget heading
    const heading = document.querySelector(
      '[data-testid="stylitics-ui-heading"]'
    );
    if (heading) {
      heading.style.visibility = "visible";
    }
  }

  /**
   * Hide the widget and jump link
   */
  hideWidget() {
    const container = document.getElementById("stylitics-composer-container");
    if (container) {
      container.style.display = "none";
    }

    const jumpLink = document.getElementById("stylitics-jumplink");
    if (jumpLink) {
      jumpLink.style.visibility = "hidden";
      jumpLink.style.display = "none";
    }
  }

  /**
   * Create jump link badge in the top right of product gallery
   */
  createJumpLinkIfNeeded() {
    if (!this.settings.showJumpLink) return;

    let jumpLink = document.getElementById("stylitics-jumplink");
    if (jumpLink) {
      // Update existing jump link to be visible
      jumpLink.style.visibility = "visible";
      jumpLink.style.display = "block";
      return;
    }

    // Look for product gallery/media containers to position the badge
    const galleryContainers = [
      // Try to find the main product media container
      document.querySelector(".product__media"),
      document.querySelector(".product-media"),
      document.querySelector("[data-media-gallery]"),
      document.querySelector(".product-gallery"),
      // Fallback to any media container
      document.querySelector(".media"),
      document.querySelector("[class*='media']"),
      // Last resort - main product section
      document.querySelector(".shopify-section--main-product"),
    ];

    const galleryContainer = galleryContainers.find((el) => el !== null);
    if (!galleryContainer) {
      console.warn(
        "Could not find suitable gallery container for Stylitics badge"
      );
      return;
    }

    // Create jump link badge positioned in top right of gallery
    jumpLink = document.createElement("a");
    jumpLink.id = "stylitics-jumplink";
    jumpLink.href = "#stylitics-composer-container";
    jumpLink.className = "stylitics-jump-badge";
    jumpLink.style.cssText = `
      position: absolute;
      z-index: 10;
      display: inline-flex;
      align-items: center;
      padding: 8px 12px;
      background: #fcfaf6;
      color: #2b2e2f;
      text-decoration: none;
      border-radius: 20px;
      font-size: 0.75rem;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      transition: all 0.3s ease;
      backdrop-filter: blur(4px);
      visibility: visible;
      box-shadow:0 0 0 1px rgb(var(--text-color) / .12);
    `;

    jumpLink.innerHTML = `
      ${this.settings.heading || "Wear It With"} 
      <svg style="margin-left: 6px; width: 10px; height: 6px;" viewBox="0 0 12 8" fill="currentColor">
        <path d="M1 1l5 5 5-5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    // Add hover effects
    jumpLink.addEventListener("mouseenter", () => {
      jumpLink.style.background = "#2b2e2f";
      jumpLink.style.color = "#FFFFFF";
      jumpLink.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.2)";
    });

    jumpLink.addEventListener("mouseleave", () => {
      jumpLink.style.background = "rgba(238, 237, 224, 0.95)";
      jumpLink.style.color = "#2b2e2f";
      jumpLink.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
    });

    // Add smooth scroll behavior with proper offset
    jumpLink.addEventListener("click", (e) => {
      e.preventDefault();
      const container = document.getElementById("stylitics-composer-container");
      if (container) {
        // Scroll with offset to account for fixed headers
        const headerOffset = 100;
        const elementPosition = container.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });

        // Track the jump link click
        if (this.composer?.modules?.[0]?.trackClickJumpLink) {
          this.composer.modules[0].trackClickJumpLink();
          console.log("Stylitics jumplink click tracked");
        }
      }
    });

    // Ensure the gallery container has relative positioning
    const galleryPosition = window.getComputedStyle(galleryContainer).position;
    if (galleryPosition === "static") {
      galleryContainer.style.position = "relative";
    }

    // Insert the badge into the gallery container
    galleryContainer.appendChild(jumpLink);
    console.log(
      "Stylitics badge created in gallery:",
      galleryContainer.className
    );
  }

  /**
   * Bind jump link tracking (legacy method - tracking now handled in createJumpLinkIfNeeded)
   */
  bindJumpLinkTracking() {
    // Jump link tracking is now handled in createJumpLinkIfNeeded method
    // This method is kept for backward compatibility
  }

  /**
   * Setup Quick Add modal interception for Stylitics product links
   */
  setupQuickAddInterception() {
    const container = document.getElementById("stylitics-composer-container");
    if (!container) return;

    // Simple debounce to prevent excessive calls
    let debounceTimeout;

    const observer = new MutationObserver(() => {
      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => {
        this.interceptProductLinks(container);
        this.setupStyliticsEventListeners(container);
      }, 500);
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
    });

    // Also check immediately in case content is already loaded
    setTimeout(() => this.interceptProductLinks(container), 1000);
  }

  /**
   * Intercept Stylitics product links and replace with Quick Add functionality
   */
  interceptProductLinks(container) {
    const productLinks = container.querySelectorAll('a[href*="/products/"]');

    // Only log once when we first find links
    if (!this.hasLoggedLinks && productLinks.length > 0) {
      this.hasLoggedLinks = true;
    }

    productLinks.forEach((link) => {
      // Skip if already processed
      if (link.dataset.styliticsProcessed === "true") return;
      // Mark as processed
      link.dataset.styliticsProcessed = "true";

      // Extract product handle and variant ID from the URL
      const url = new URL(link.href);
      const pathParts = url.pathname.split("/");
      const handle = pathParts[pathParts.indexOf("products") + 1];

      // Extract variant ID from URL params or data attributes
      let variantId = url.searchParams.get("variant");
      if (!variantId) {
        // Try to find variant ID in data attributes or other sources
        variantId = link.dataset.variantId || link.dataset.variant;
      }

      // Replace click behavior with Quick Add modal
      link.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Dispatch the quick-add:open event that the theme listens for
        window.dispatchEvent(
          new CustomEvent("quick-add:open", {
            detail: {
              handle: handle,
              variantId: variantId || null,
            },
          })
        );

        // Track the click for analytics
        this.trackProductClick(handle, variantId, {
          source: "stylitics_intercepted_link",
          originalUrl: link.href,
        });

        // Mark this as a Stylitics interaction for tracking QuickView add-to-carts
        this._lastStyliticsInteraction = Date.now();
      });

      // Add visual indicator that this will open a modal
      this.addQuickAddIndicator(link);
    });
  }

  /**
   * Add visual indicator to show this link opens a modal
   */
  addQuickAddIndicator(link) {
    // Add a small icon or styling to indicate this opens a modal
    const existingIcon = link.querySelector(".stylitics-modal-icon");
    if (existingIcon) return; // Already has indicator

    // Create a small modal icon
    const icon = document.createElement("span");
    icon.className = "stylitics-modal-icon";
    icon.style.cssText = `
      position: absolute;
      top: 4px;
      right: 4px;
      width: 16px;
      height: 16px;
      background: rgba(35, 62, 73, 0.8);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 5;
    `;

    icon.innerHTML = `
      <svg width="8" height="8" viewBox="0 0 12 12" fill="white">
        <path d="M2 2h8v8H2V2zm1 1v6h6V3H3z" fill="white"/>
        <path d="M4 4h4v4H4V4z" fill="white"/>
      </svg>
    `;

    // Find the best parent element to append the icon
    const imageContainer = link.querySelector("img")?.parentElement || link;
    if (
      imageContainer &&
      window.getComputedStyle(imageContainer).position === "static"
    ) {
      imageContainer.style.position = "relative";
    }

    imageContainer.appendChild(icon);
  }

  /**
   * Handle Stylitics product click events
   */
  handleProductClick(event) {
    console.log("Stylitics product click:", event);

    const itemId = event.productId || event.item_number;
    if (itemId && this.composer?.modules?.[0]?.trackClickItem) {
      this.composer.modules[0].trackClickItem(itemId);
      console.log("✅ Stylitics trackClickItem fired:", itemId);
    }
  }

  /**
   * Handle Stylitics hotspot click events
   */
  handleHotspotClick(event) {
    console.log("Stylitics hotspot click:", event);

    const itemId = event.productId || event.item_number;
    if (itemId && this.composer?.modules?.[0]?.trackClickItem) {
      this.composer.modules[0].trackClickItem(itemId);
      console.log("✅ Stylitics trackClickItem fired (hotspot):", itemId);
    }
  }

  /**
   * Handle Stylitics add-to-cart click events
   */
  handleAddToCartClick(event) {
    console.log("Stylitics add-to-cart click:", event);

    const itemId = event.productId || event.item_number;
    if (itemId && this.composer?.modules?.[0]?.trackAddItemToCart) {
      this.composer.modules[0].trackAddItemToCart(itemId);
      console.log("Stylitics trackAddItemToCart fired:", itemId);
    }
  }

  /**
   * Track product click for analytics
   */
  trackProductClick(handle, variantId, productInfo = {}) {
    // Track with Stylitics native API
    if (this.composer?.modules?.[0]?.trackClickItem) {
      try {
        this.composer.modules[0].trackClickItem({
          item_number: productInfo.productId || handle,
          variant_id: variantId,
          bundle_id: productInfo.bundleId,
          placement: "pdp",
          source: productInfo.source || "product_list",
        });
        console.log("Stylitics item click tracked:", {
          handle,
          variantId,
          bundleId: productInfo.bundleId,
        });
      } catch (error) {
        console.warn("Stylitics trackClickItem error:", error);
      }
    } else {
      console.warn("Stylitics trackClickItem method not available");
    }
  }

  /**
   * Track hotspot click for analytics
   */
  trackHotspotClick(hotspotInfo) {
    // Track with Stylitics native API
    if (this.composer?.modules?.[0]?.trackClickHotspot) {
      try {
        this.composer.modules[0].trackClickHotspot({
          bundle_id: hotspotInfo.bundleId,
          item_number: hotspotInfo.productId,
          position: hotspotInfo.position,
          placement: "pdp",
        });
        console.log("✅ Stylitics hotspot click tracked:", {
          bundleId: hotspotInfo.bundleId,
          productId: hotspotInfo.productId,
        });
      } catch (error) {
        console.warn("Stylitics trackClickHotspot error:", error);
      }
    } else {
      console.warn("Stylitics trackClickHotspot method not available");
    }
  }

  /**
   * Track add-to-cart for analytics
   */
  trackAddToCart(cartInfo) {
    // Track with Stylitics native API - try trackAddItemToCart first, then fallback to trackClick
    const module = this.composer?.modules?.[0];

    if (module?.trackAddItemToCart) {
      try {
        module.trackAddItemToCart({
          item_number: cartInfo.productId,
          variant_id: cartInfo.variantId,
          quantity: cartInfo.quantity,
          price: cartInfo.priceInCents || cartInfo.price * 100, // Stylitics expects price in cents
          placement: "pdp",
          source: cartInfo.source,
        });
        console.log("Stylitics add-to-cart tracked (trackAddItemToCart):", {
          productId: cartInfo.productId,
          variantId: cartInfo.variantId,
          quantity: cartInfo.quantity,
          source: cartInfo.source,
        });
      } catch (error) {
        console.warn("Stylitics trackAddItemToCart error:", error);
      }
    } else {
      console.warn("Stylitics trackAddItemToCart method not available");
    }
  }

  /**
   * Refresh widget for new product
   */
  async refresh(newProductId) {
    if (!this.isInitialized || !this.composer) {
      return this.init();
    }

    if (this.productId === newProductId) {
      return;
    }

    this.productId = newProductId;

    try {
      await this.composer.refresh({
        modules: [
          {
            type: "classic",
            params: {
              api: {
                item_number: newProductId,
                min: this.settings.minBundles || 3,
                max: this.settings.maxBundles || 6,
              },
            },
          },
        ],
      });
    } catch (error) {
      console.error("Stylitics: Refresh failed:", error);
    }
  }

  /**
   * Setup listener for product changes when color swatches are clicked
   */
  setupVariantChangeListener() {
    // Hook directly into swatch clicks
    document.addEventListener("click", (event) => {
      const swatchElement =
        event.target.closest(".product-swatch") ||
        event.target.closest('[class*="swatch"]') ||
        event.target.closest(".variant-picker button") ||
        event.target.closest(".product-grouping button");

      if (swatchElement) {
        setTimeout(() => {
          const newProductId = this.getUpdatedProductId();
          if (newProductId && newProductId !== this.productId) {
            this.refresh(newProductId);
          }
        }, 1000);
      }
    });
  }

  /**
   * Get the updated product ID from the DOM after color swatch changes
   */
  getUpdatedProductId() {
    const mainProductInfo = document.querySelector("#main-product-info");
    if (mainProductInfo) {
      const productId = mainProductInfo.getAttribute("data-product-id");
      if (productId) {
        return productId;
      }
    }

    if (window.ShopifyAnalytics?.meta?.product?.id) {
      return window.ShopifyAnalytics.meta.product.id.toString();
    }

    return null;
  }

  /**
   * Setup additional Stylitics event listeners for tracking
   */
  setupStyliticsEventListeners(container) {
    // Additional click tracking for any elements that might not be caught by Stylitics events
    container.addEventListener("click", (event) => {
      const target = event.target;

      // Track clicks on hotspot elements
      const hotspotElement = target.closest(
        "[data-stylitics-hotspot], .stylitics-hotspot"
      );
      if (hotspotElement) {
        this.trackHotspotClick({
          bundleId: hotspotElement.dataset.bundleId,
          productId: hotspotElement.dataset.productId,
          element: hotspotElement,
        });
      }

      // Track clicks on product elements within Stylitics widget
      const productElement = target.closest(
        "[data-stylitics-product], .stylitics-product"
      );
      if (productElement) {
        this.trackProductClick(
          productElement.dataset.handle,
          productElement.dataset.variantId,
          {
            productId: productElement.dataset.productId,
            element: productElement,
          }
        );
      }
    });
  }

  /**
   * Setup tracking for add-to-cart events from the main theme
   */
  setupAddToCartTracking() {
    // Listen for Shopify's variant:add events (from main theme)
    document.addEventListener("variant:add", (event) => {
      console.log("variant:add event detected:", event);

      // Check if this add-to-cart came from Stylitics widget
      const styliticsContainer = document.getElementById(
        "stylitics-composer-container"
      );

      console.log("Stylitics container:", styliticsContainer);
      console.log("Event target:", event.target);
      console.log(
        "Container contains target:",
        styliticsContainer &&
          event.target &&
          styliticsContainer.contains(event.target)
      );

      if (
        styliticsContainer &&
        event.target &&
        styliticsContainer.contains(event.target)
      ) {
        console.log("Add-to-cart from Stylitics widget detected!");
        const detail = event.detail;
        const items = detail.items || [detail];

        console.log("Cart items to track:", items);

        items.forEach((item) => {
          this.trackAddToCart({
            productId: item.product_id,
            variantId: item.variant_id,
            quantity: item.quantity,
            price: item.price ? item.price / 100 : null, // Convert cents to dollars
            priceInCents: item.price, // Keep original for reference
            handle: item.handle,
            source: "stylitics_widget_variant_add",
          });
        });
      } else {
        console.log("Add-to-cart not from Stylitics widget, ignoring");
      }
    });

    // Listen for your native QuickView modal events
    document.addEventListener("quick-add:open", (event) => {
      // Mark that a QuickView was opened from Stylitics
      if (
        this._lastStyliticsInteraction &&
        Date.now() - this._lastStyliticsInteraction < 2000
      ) {
        this._quickViewFromStylitics = true;
        this._quickViewOpenTime = Date.now();
        console.log(
          "✅ QuickView opened from Stylitics - will track add-to-cart"
        );
      }
    });

    // Also listen for variant:add events from QuickView
    document.addEventListener("variant:add", (event) => {
      console.log("variant:add event detected:", event);

      // Debug QuickView tracking state
      console.log(
        "🔍 QuickView from Stylitics flag:",
        this._quickViewFromStylitics
      );
      console.log("🔍 QuickView open time:", this._quickViewOpenTime);
      console.log(
        "🔍 Time since QuickView opened:",
        this._quickViewOpenTime
          ? Date.now() - this._quickViewOpenTime + "ms"
          : "N/A"
      );

      // If QuickView was opened from Stylitics, track this add-to-cart
      if (
        this._quickViewFromStylitics &&
        this._quickViewOpenTime &&
        Date.now() - this._quickViewOpenTime < 60000
      ) {
        // 60 second window

        console.log("🛒 Add-to-cart from Stylitics QuickView detected!");

        const detail = event.detail;
        const items = detail.items || [detail];
        console.log("Cart items to track:", items);

        items.forEach((item) => {
          const itemId = item.product_id;
          if (itemId && this.composer?.modules?.[0]?.trackAddItemToCart) {
            this.composer.modules[0].trackAddItemToCart(itemId);
            console.log("✅ Stylitics trackAddItemToCart fired:", itemId);
          }
        });

        // Clear the flag after tracking
        this._quickViewFromStylitics = false;
        this._quickViewOpenTime = null;
      } else {
        // Check if this was a recent Stylitics interaction (backup method)
        const recentStyliticsActivity =
          this._lastStyliticsInteraction &&
          Date.now() - this._lastStyliticsInteraction < 10000; // 10 second window

        console.log("🔍 Recent Stylitics activity:", recentStyliticsActivity);
        console.log(
          "🔍 Last interaction time:",
          this._lastStyliticsInteraction
        );
        console.log(
          "🔍 Time since last interaction:",
          this._lastStyliticsInteraction
            ? Date.now() - this._lastStyliticsInteraction + "ms"
            : "N/A"
        );

        if (recentStyliticsActivity) {
          console.log(
            "🛒 Add-to-cart from recent Stylitics interaction detected!"
          );

          const detail = event.detail;
          const items = detail.items || [detail];
          console.log("Cart items to track:", items);

          items.forEach((item) => {
            const itemId = item.product_id;
            if (itemId && this.composer?.modules?.[0]?.trackAddItemToCart) {
              this.composer.modules[0].trackAddItemToCart(itemId);
              console.log("✅ Stylitics trackAddItemToCart fired:", itemId);
            }
          });
        } else {
          console.log(
            "ℹ️ Add-to-cart not from Stylitics (no recent interaction)"
          );
        }
      }
    });

    // Track all clicks within Stylitics widget for debugging
    const styliticsContainer = document.getElementById(
      "stylitics-composer-container"
    );
    if (styliticsContainer) {
      styliticsContainer.addEventListener("click", (event) => {
        console.log("Click in Stylitics widget:", event.target);
        this._lastStyliticsInteraction = Date.now();

        // Look for various types of add-to-cart buttons
        const possibleButtons = [
          '[data-action="add-to-cart"]',
          ".add-to-cart",
          'button[type="submit"]',
          ".btn-cart",
          ".product-form button",
          '[data-testid*="add"]',
          ".quick-add-button",
          ".stylitics-add-to-cart",
        ];

        const addToCartButton = event.target.closest(
          possibleButtons.join(", ")
        );

        if (addToCartButton) {
          console.log(
            "Add-to-cart button clicked in Stylitics:",
            addToCartButton
          );

          // Try to extract product information from the button or its context
          const form = addToCartButton.closest("form");
          if (form) {
            console.log("Form found:", form);
            const variantInput = form.querySelector(
              'input[name="id"], input[name="variant"], input[name="variant_id"]'
            );
            const quantityInput = form.querySelector('input[name="quantity"]');

            console.log("Variant input:", variantInput);
            console.log("Quantity input:", quantityInput);

            if (variantInput) {
              console.log("Tracking add-to-cart from form submission");
              this.trackAddToCart({
                variantId: variantInput.value,
                quantity: quantityInput ? quantityInput.value : 1,
                source: "stylitics_widget_form_click",
              });
            }
          }

          // Also check for data attributes on the button itself
          const variantId =
            addToCartButton.dataset.variantId ||
            addToCartButton.dataset.variant ||
            addToCartButton.getAttribute("data-variant-id");
          const productId =
            addToCartButton.dataset.productId ||
            addToCartButton.dataset.product ||
            addToCartButton.getAttribute("data-product-id");

          if (variantId || productId) {
            console.log("Tracking add-to-cart from button data attributes");
            this.trackAddToCart({
              variantId: variantId,
              productId: productId,
              quantity: 1,
              source: "stylitics_widget_button_click",
            });
          }
        }
      });
    }
  }
}

// Initialize widget when DOM is ready
(function () {
  let styliticsWidget;

  function initWidget() {
    if (!styliticsWidget) {
      styliticsWidget = new StyliticsWidget();
      // Expose instance for manual debugging after creation
      window.styliticsWidget = styliticsWidget;
    }
    styliticsWidget.init();
  }

  // Multiple initialization triggers to ensure the widget loads
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initWidget);
  } else {
    initWidget();
  }

  // Also initialize on theme section loads (for AJAX navigation)
  document.addEventListener("shopify:section:load", (e) => {
    if (e.detail.sectionId.includes("main-product")) {
      setTimeout(initWidget, 100);
    }
  });

  // Expose class for manual debugging
  window.StyliticsWidget = StyliticsWidget;

  // Expose test function for debugging tracking events
  window.testStyliticsTracking = function () {
    console.log("Testing Stylitics tracking implementation...");

    const widget = window.styliticsWidget;
    if (!widget) {
      console.error("Stylitics widget not found. Make sure it is initialized.");
      return;
    }

    console.log("Product ID:", widget.productId);
    console.log("Composer available:", !!widget.composer);
    console.log("Settings:", widget.settings);

    // Debug customer data detection
    console.log("Customer Data Sources:");
    console.log("- window.styliticsCustomer:", window.styliticsCustomer);
    console.log("- window.__VPV?.customer:", window.__VPV?.customer);
    console.log("- window.cnstrc?.userId:", window.cnstrc?.userId);

    // Final resolved values
    const customerData = window.styliticsCustomer || {};
    const resolvedCustomerId =
      customerData.id ||
      window.__VPV?.customer?.id ||
      window.cnstrc?.userId ||
      null;
    const resolvedCustomerEmail =
      customerData.email || window.__VPV?.customer?.email || null;

    console.log("Resolved Customer Data:");
    console.log("- Customer ID:", resolvedCustomerId);
    console.log("- Customer Email:", resolvedCustomerEmail);

    // Test tracking methods
    console.log("Testing product click tracking...");
    widget.trackProductClick("test-handle", "12345", {
      productId: "test-product",
      bundleId: "test-bundle",
      source: "manual_test",
    });

    console.log("Testing hotspot click tracking...");
    widget.trackHotspotClick({
      bundleId: "test-bundle",
      position: { x: 100, y: 200 },
      productId: "test-product",
    });

    console.log("Testing add-to-cart tracking...");
    widget.trackAddToCart({
      productId: "test-product",
      variantId: "12345",
      quantity: 1,
      price: 59.99,
      source: "manual_test",
    });

    console.log(
      "Tracking test completed. Check console and network tab for events."
    );
  };

  // Expose add-to-cart debugging function
  window.debugStyliticsAddToCart = function () {
    console.log("=== Stylitics Add-to-Cart Debug ===");

    const container = document.getElementById("stylitics-composer-container");
    console.log("Stylitics container:", container);

    if (!container) {
      console.error("Stylitics container not found!");
      return;
    }

    // Find all potential add-to-cart elements
    const buttons = container.querySelectorAll(
      'button, [data-action="add-to-cart"], .add-to-cart, input[type="submit"], .btn, .quick-add'
    );
    console.log("Found potential buttons:", buttons.length);

    buttons.forEach((btn, index) => {
      console.log(`Button ${index + 1}:`, {
        element: btn,
        text: btn.textContent?.trim(),
        classes: btn.className,
        dataAction: btn.dataset.action,
        type: btn.type,
        form: btn.closest("form"),
      });
    });

    // Find all forms
    const forms = container.querySelectorAll("form");
    console.log("Found forms:", forms.length);

    forms.forEach((form, index) => {
      const variantInput = form.querySelector(
        'input[name="id"], input[name="variant"], input[name="variant_id"]'
      );
      const quantityInput = form.querySelector('input[name="quantity"]');
      console.log(`Form ${index + 1}:`, {
        form: form,
        action: form.action,
        variantInput: variantInput,
        variantValue: variantInput?.value,
        quantityInput: quantityInput,
        quantityValue: quantityInput?.value,
      });
    });

    // Check for recent interactions
    const widget = window.styliticsWidget;
    console.log(
      "Last Stylitics interaction:",
      widget?._lastStyliticsInteraction
    );
    console.log(
      "Time since last interaction:",
      widget?._lastStyliticsInteraction
        ? Date.now() - widget._lastStyliticsInteraction + "ms"
        : "No recent interaction"
    );
    console.log("QuickView from Stylitics:", widget?._quickViewFromStylitics);
    console.log("QuickView open time:", widget?._quickViewOpenTime);
  };

  // Expose Stylitics API debugging function
  window.debugStyliticsAPI = function () {
    console.log("=== Stylitics API Methods Debug ===");

    const widget = window.styliticsWidget;
    const composer = widget?.composer;
    const module = composer?.modules?.[0];

    console.log("Widget:", widget);
    console.log("Composer:", composer);
    console.log("Module:", module);

    if (module) {
      console.log("Available tracking methods:");
      console.log("- trackClickItem:", typeof module.trackClickItem);
      console.log("- trackClickHotspot:", typeof module.trackClickHotspot);
      console.log("- trackAddItemToCart:", typeof module.trackAddItemToCart);
      console.log("- trackClickJumpLink:", typeof module.trackClickJumpLink);
      console.log("- trackView:", typeof module.trackView);
      console.log("- track:", typeof module.track);

      console.log("All methods on module:");
      Object.getOwnPropertyNames(module).forEach((prop) => {
        if (typeof module[prop] === "function" && prop.startsWith("track")) {
          console.log(`- ${prop}:`, typeof module[prop]);
        }
      });
    } else {
      console.error("Stylitics module not found!");
    }
  };
})();
